import _ from "lodash";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Field, useField, useForm } from "react-final-form";
import { ConfirmModal } from "../components/ConfirmModal";
import { Fieldset } from "../components/inputs/final_form/Fieldset";
import { SelectInput } from "../components/inputs/final_form/SelectInput";
import { TextInput } from "../components/inputs/final_form/TextInput";
import { LoadingButton } from "../components/LoadingButton";
import { api, redirectTo } from "../util/api";
import { snakifyKeys } from "../util/helpers";
import { numericInRange, required } from "../util/validators";
import { trackEvent } from "../util/track";
import { Card, CardText } from "reactstrap";
import { StatusTheCaseFields } from "../submit_right_direction/components/StatusTheCaseFields";

const REQUIRED_STATUS_FIELDS = {
  approved: ["authorizationNumber", "authorizationDate"],
  denied: [],
  documented_clean_claim: ["submissionReferenceNumber"],
  medicalreview: [],
  partially_approved: ["authorizationNumber", "authorizationDate", "approvedCptCodes"],
  submitted: [],
  rejected: [],
};

const DISPLAY_STATUS_FIELDS = {
  approved: ["authorizationNumber", "authorizationDate", "authorizationEffectiveDate", "authorizationExpirationDate"],
  denied: ["denialReasonCode", "submissionReferenceNumber"],
  documented_clean_claim: ["submissionReferenceNumber"],
  medicalreview: ["submissionReferenceNumber", "expectedCloseDate"],
  partially_approved: [
    "authorizationNumber",
    "authorizationDate",
    "authorizationEffectiveDate",
    "authorizationExpirationDate",
    "approvedCptCodes",
    "deniedCptCodes",
    "denialReasonCode",
  ],
  code_specific_result: ["approvedCptCodes", "deniedCptCodes", "documentedCleanClaimCptCodes"],
  submitted: [],
  rejected: [],
};

const ConfirmationButton = ({ children, showModal = false, loading, disabled, onClick, onModalConfirm, ...rest }) => {
  const confirmButton = (
    <LoadingButton type="button" color="primary" loading={loading} disabled={disabled} onClick={onClick} {...rest}>
      {children}
    </LoadingButton>
  );

  return showModal ? (
    <ConfirmModal
      Target={confirmButton}
      onConfirm={onModalConfirm}
      confirmLabel="Confirm"
      title="Proceed to Next Submission"
      confirmMessage="Confirming this response will move the workflow to the next submission. Are you sure?"
      confirmButtonProps={{ type: "button" }}
    />
  ) : (
    confirmButton
  );
};

const trackProceedNextSubmission = (caseId) => {
  trackEvent("proceed_to_next_submission_confirm_button_sr2_phone", caseId, {
    step: "proceed_to_next_submission_confirm_button_sr2_phone",
  });
};

export const SubmitRightPhoneResponseForm = ({
  caseId,
  request,
  getPhoneResponseObj,
  phoneResponseOptions,
  denialReasonCodeOptions,
  verifiedCptCodeOptions,
  paStatuses,
  setConfirmedPhoneResponse,
  setError,
  setShowFaxButton,
  nextSubmissionUrl,
}) => {
  const form = useForm();
  const phoneResponse = useField("phoneSubmissionResponse.message", { subscription: { value: true } })?.input?.value;

  const approvedCptCodesValue = useField("phoneSubmissionResponse.approvedCptCodes", { subscription: { value: true } })?.input?.value;
  const deniedCptCodesValue = useField("phoneSubmissionResponse.deniedCptCodes", { subscription: { value: true } })?.input?.value;
  const cleanClaimCptCodesValue = useField("phoneSubmissionResponse.documentedCleanClaimCptCodes", { subscription: { value: true } })?.input
    ?.value;

  const [confirmed, _setConfirmed] = useState(false);
  const [loading, setLoading] = useState(false);

  const setConfirmed = useCallback(
    (value) => {
      _setConfirmed(value);

      // Also update the parent copy
      setConfirmedPhoneResponse(value);
    },
    [setConfirmedPhoneResponse]
  );

  // Upon choosing a phone response, we clear out the confirmation.
  // We don't want this hook to fire any other time; the linter is wrong here.
  useEffect(() => {
    if (confirmed) {
      setConfirmed(false);
      setShowFaxButton(false);
    }
  }, [phoneResponse]); // eslint-disable-line react-hooks/exhaustive-deps

  const responseObj = !_.isEmpty(phoneResponse) && getPhoneResponseObj(phoneResponse);
  const { status } = responseObj;

  const parseStatusAndSubstatus = (responseObj) => {
    const parsedStatus = responseObj.status;
    const parsedSubstatus = responseObj.substatus;

    form.change("phoneSubmissionResponse.status", parsedStatus);
    form.change("phoneSubmissionResponse.substatus", parsedSubstatus);
  };

  // other handlers
  const isCodeSpecificResult = useMemo(() => status === "code_specific_result", [status]);

  const handleConfirm = () => {
    setLoading(true);

    if (responseObj?.message !== undefined) {
      form.change("phoneSubmissionResponse.phoneResponseId", responseObj.id);
      form.change("phoneSubmissionResponse.cannedComment", responseObj.canned_comment);

      if (responseObj.submit_right_direction == "status_the_case") {
        parseStatusAndSubstatus(responseObj);

        if (responseObj?.denial_reason_code) {
          form.change("phoneSubmissionResponse.denialReasonCode", responseObj?.denial_reason_code);
        }
      } else if (responseObj.submit_right_direction == "task_out_for_n_number_of_days_to_try_again") {
        form.change("phoneSubmissionResponse.taskName", responseObj.task_name);
        form.change("phoneSubmissionResponse.taskDuration", responseObj.task_duration);

        trackEvent("task_out_case_sr2_phone_response", caseId, {
          step: "task_out_case_sr2_phone_response",
        });
      }
    }

    trackEvent("phone_submission_confirm_button", caseId, {
      step: "phone_submission_confirm_button",
    });

    setConfirmed(true);
    setShowFaxButton(["submitted", "medicalreview"].includes(responseObj.status));
    setLoading(false);
  };

  const confirmProceedNextSubmission = async (cannedComment) => {
    setLoading(true);
    setError(null);

    const params = {
      id: caseId,
      proceedNextSubmission: true,
      phoneSubmissionResponse: {
        phoneResponseId: responseObj.id,
        cannedComment: cannedComment,
      },
    };

    try {
      await api.post(nextSubmissionUrl, snakifyKeys(params));
      trackProceedNextSubmission(caseId);
      redirectTo("/workflow/" + caseId);
    } catch (err) {
      setError(err);
      setLoading(false);
    }
  };

  return (
    <>
      {(responseObj?.status === "code_specific_result" || responseObj?.status === "partially_approved") &&
        verifiedCptCodeOptions.length === 1 && (
          <Card color="warning" className="text-center py-4 bg-opacity-10 border-0 mb-2">
            <CardText>This Case only has 1 CPT Code, use the correct Submit Right Response instead.</CardText>
          </Card>
        )}

      <Fieldset>
        <Field
          component={SelectInput}
          label="Phone Submission Response"
          name="phoneSubmissionResponse.message"
          options={phoneResponseOptions}
        ></Field>

        {confirmed && responseObj?.submit_right_direction === "task_out_for_n_number_of_days_to_try_again" && (
          <>
            <Field component={TextInput} label="Task Name" name="phoneSubmissionResponse.taskName" disabled={true} />
            <Field
              component={TextInput}
              type="number"
              name="phoneSubmissionResponse.taskDuration"
              label="Task Duration(Days)"
              validate={numericInRange(0, 90)}
              min="0"
              max="90"
              disabled={true}
            />
          </>
        )}

        {confirmed && responseObj?.submit_right_direction === "status_the_case" && (
          <>
            <StatusTheCaseFields
              verifiedCptCodeOptions={verifiedCptCodeOptions}
              isCodeSpecificResult={isCodeSpecificResult}
              status={status}
              displayStatusMap={DISPLAY_STATUS_FIELDS}
              requiredStatusMap={REQUIRED_STATUS_FIELDS}
              formLink={"phoneSubmissionResponse"}
              request={request}
              denialReasonCodeOptions={denialReasonCodeOptions}
              messageObj={responseObj}
              approvedCptCodes={approvedCptCodesValue}
              deniedCptCodes={deniedCptCodesValue}
              cleanClaimCptCodes={cleanClaimCptCodesValue}
            />
          </>
        )}

        {confirmed && responseObj && (
          <>
            <Field component={TextInput} label="Canned Comment" name={"phoneSubmissionResponse.cannedComment"} />
          </>
        )}

        {!confirmed && (
          <ConfirmationButton
            showModal={responseObj?.submit_right_direction === "proceed_to_the_next_submission"}
            onClick={handleConfirm}
            onModalConfirm={() => confirmProceedNextSubmission(responseObj.canned_comment)}
            loading={loading}
            disabled={loading || _.isEmpty(phoneResponse)}
          >
            Confirm Response
          </ConfirmationButton>
        )}
      </Fieldset>
    </>
  );
};
